/**
 * notificationSettingsConfig.js
 *
 * Metadata for every automated notification that can be toggled in the
 * Notification Preferences panel of ProviderSettings.  Organised into
 * three audience groups that mirror the Firestore notificationConfig
 * structure stored on each organisation document.
 *
 * Each entry maps to a key inside organisations/{orgId}.notificationConfig
 * and provides the human-readable label, trigger description (shown in an
 * InfoTooltip), and flags that control the UI:
 *
 *   - label:         Short name shown next to the toggle.
 *   - description:   Tooltip text explaining when this notification fires.
 *   - allowDisable:  If false, the "Send" toggle is always on and disabled
 *                    (e.g. self-harm concern — safety-critical).
 *   - hasCcProvider: Whether a "CC Provider" toggle should be shown.
 *                    Only meaningful for patient/informant groups;
 *                    providerAlerts never have a CC column since the
 *                    provider IS the recipient.
 */

// ─── Patient Notifications ─────────────────────────────────────────────
// Lifecycle and reminder emails sent TO the patient.  The first two
// (welcome + setup success) are always-send — they can't be suppressed
// because the patient literally needs them to access the platform.
// Only the "CC Provider" toggle is meaningful for those entries.
export const PATIENT_NOTIFICATIONS = [
  {
    key: 'patient_welcome',
    label: 'Welcome Email (Password Setup Link)',
    description:
      'Sent immediately when a patient account is created. Contains the secure link for the patient to set their password.',
    allowDisable: false,
    hasCcProvider: true,
  },
  {
    key: 'patient_setup_success',
    label: 'Account Setup Confirmation',
    description:
      'Sent after the patient successfully sets their password. Contains the link to log in and complete their questionnaires.',
    allowDisable: false,
    hasCcProvider: true,
  },
  {
    key: 'account_setup_reminder_day_3',
    label: 'Account Setup Reminder – Day 3',
    description:
      'Sent 3 days after a patient account is created if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'account_setup_reminder_day_6',
    label: 'Account Setup Reminder – Day 6',
    description:
      'Sent 6 days after a patient account is created if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'account_setup_reminder_day_9',
    label: 'Account Setup Reminder – Day 9',
    description:
      'Sent 9 days after a patient account is created if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'questionnaire_reminder_day_3',
    label: 'Questionnaire Reminder – Day 3',
    description:
      'Sent 3 days after the patient first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'questionnaire_reminder_day_6',
    label: 'Questionnaire Reminder – Day 6',
    description:
      'Sent 6 days after the patient first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'questionnaire_reminder_day_9',
    label: 'Questionnaire Reminder – Day 9',
    description:
      'Sent 9 days after the patient first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
  // SLaM / 3 Borough only: crisis-support resources email sent TO the patient
  // when they indicate potential self-harm on PHQ-9 Q9. This row is display-only
  // (allowDisable: false) and is never stored in notificationConfig — the backend
  // send is hardwired. The `orgs` array gates visibility so only SLaM / 3 Borough
  // providers see the row in their Notification Preferences panel.
  {
    key: 'self_harm_patient_support',
    label: 'Self-Harm Crisis Support (Patient)',
    description:
      'Sent immediately to the patient when they indicate potential self-harm in the PHQ-9 questionnaire. Contains NHS crisis resources (NHS 111, SHOUT, SLaM WhatsApp). The support inbox is always CC\'d. This notification cannot be disabled for safety reasons.',
    allowDisable: false,
    hasCcProvider: false,
    orgs: ['SLaM', '3 Borough Adult ADHD and Autism Service'],
  },
];

// ─── Informant Notifications ───────────────────────────────────────────
// Lifecycle and reminder emails sent TO the informant (parent/carer/
// teacher etc.).  Welcome + setup success are always-send (same rationale
// as patients).
export const INFORMANT_NOTIFICATIONS = [
  {
    key: 'informant_welcome',
    label: 'Welcome Email (Password Setup Link)',
    description:
      'Sent immediately when an informant is added. Contains the secure link for the informant to set their password and complete questionnaires for the patient\'s assessment.',
    allowDisable: false,
    hasCcProvider: true,
  },
  {
    key: 'informant_setup_success',
    label: 'Account Setup Confirmation',
    description:
      'Sent after the informant successfully sets their password. Contains the link to log in and complete their questionnaires.',
    allowDisable: false,
    hasCcProvider: true,
  },
  {
    key: 'informant_account_setup_reminder_day_3',
    label: 'Account Setup Reminder – Day 3',
    description:
      'Sent 3 days after an informant account is created if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'informant_account_setup_reminder_day_6',
    label: 'Account Setup Reminder – Day 6',
    description:
      'Sent 6 days after an informant account is created if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'informant_account_setup_reminder_day_9',
    label: 'Account Setup Reminder – Day 9',
    description:
      'Sent 9 days after an informant account is created if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'informant_questionnaire_reminder_day_3',
    label: 'Questionnaire Reminder – Day 3',
    description:
      'Sent 3 days after the informant first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'informant_questionnaire_reminder_day_6',
    label: 'Questionnaire Reminder – Day 6',
    description:
      'Sent 6 days after the informant first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'informant_questionnaire_reminder_day_9',
    label: 'Questionnaire Reminder – Day 9',
    description:
      'Sent 9 days after the informant first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
];

// ─── CYP ADHD Parent/Carer Notifications ───────────────────────────────
// Pathway-scoped group: lifecycle and reminder emails sent TO the CYP ADHD
// parent/carer (legalGuardian). Kept separate from the adult informant group
// so CYP reminder behaviour can be configured without touching adult
// pathways. Only shown for organisations with the cypAdhd module.
export const CYP_GUARDIAN_NOTIFICATIONS = [
  {
    key: 'cyp_guardian_welcome',
    label: 'Welcome Email (Password Setup Link)',
    description:
      'Sent immediately when a parent/carer account is created for a CYP ADHD assessment. Contains the secure link for them to set their password.',
    allowDisable: false,
    hasCcProvider: true,
  },
  {
    key: 'cyp_guardian_account_setup_reminder_day_3',
    label: 'Account Setup Reminder – Day 3',
    description:
      'Sent 3 days after the parent/carer account is created if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_guardian_account_setup_reminder_day_6',
    label: 'Account Setup Reminder – Day 6',
    description:
      'Sent 6 days after the parent/carer account is created if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_guardian_account_setup_reminder_day_9',
    label: 'Account Setup Reminder – Day 9',
    description:
      'Sent 9 days after the parent/carer account is created if they have not yet logged in. This is the final automated reminder.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_guardian_questionnaire_reminder_day_3',
    label: 'Questionnaire Reminder – Day 3',
    description:
      'Sent 3 days after the parent/carer first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_guardian_questionnaire_reminder_day_6',
    label: 'Questionnaire Reminder – Day 6',
    description:
      'Sent 6 days after the parent/carer first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_guardian_questionnaire_reminder_day_9',
    label: 'Questionnaire Reminder – Day 9',
    description:
      'Sent 9 days after the parent/carer first logs in if their questionnaires are not yet complete. This is the final automated reminder.',
    allowDisable: true,
    hasCcProvider: true,
  },
];

// ─── CYP ADHD School Representative Notifications ──────────────────────
// Lifecycle and reminder emails sent TO the nominated school representative
// (SENCO / class teacher). Copy uses the "about the child, from the school"
// voice. At most one automated email per day per child goes to school staff
// (the 3/6/9 lifecycle sends a single reminder on its day).
export const CYP_SCHOOL_REP_NOTIFICATIONS = [
  {
    key: 'cyp_school_rep_welcome',
    label: 'Nomination Welcome Email (Password Setup Link)',
    description:
      'Sent immediately when a school representative is nominated. Frames the request around the child and their school, and contains the secure password setup link.',
    allowDisable: false,
    hasCcProvider: true,
  },
  {
    key: 'cyp_school_rep_account_setup_reminder_day_3',
    label: 'Account Setup Reminder – Day 3',
    description:
      'Sent 3 days after the school representative is nominated if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_school_rep_account_setup_reminder_day_6',
    label: 'Account Setup Reminder – Day 6',
    description:
      'Sent 6 days after the school representative is nominated if they have not yet logged in.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_school_rep_account_setup_reminder_day_9',
    label: 'Account Setup Reminder – Day 9',
    description:
      'Sent 9 days after the school representative is nominated if they have not yet logged in. This is the final automated reminder.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_school_rep_questionnaire_reminder_day_3',
    label: 'Questionnaire Reminder – Day 3',
    description:
      'Sent 3 days after the school representative first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_school_rep_questionnaire_reminder_day_6',
    label: 'Questionnaire Reminder – Day 6',
    description:
      'Sent 6 days after the school representative first logs in if their questionnaires are not yet complete.',
    allowDisable: true,
    hasCcProvider: true,
  },
  {
    key: 'cyp_school_rep_questionnaire_reminder_day_9',
    label: 'Questionnaire Reminder – Day 9',
    description:
      'Sent 9 days after the school representative first logs in if their questionnaires are not yet complete. This is the final automated reminder.',
    allowDisable: true,
    hasCcProvider: true,
  },
];

// ─── Provider Alerts ───────────────────────────────────────────────────
// Notifications sent TO the provider team (support inbox or lead
// clinician) when certain patient/informant milestones are reached.
// These never have a "CC Provider" toggle because the provider IS the
// direct recipient.
export const PROVIDER_ALERT_NOTIFICATIONS = [
  {
    key: 'patient_first_login',
    label: 'Patient First Login',
    description:
      'Sent to the support team when a patient logs in for the first time, including a summary of their questionnaire progress.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'login_summary',
    label: 'Login Summary',
    description:
      'Sent to the support team with a detailed breakdown of questionnaire progress each time a patient logs in.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'patient_questionnaires_completed',
    label: 'Patient Questionnaires Completed',
    description:
      'Sent to the support team when a patient finishes all of their assigned questionnaires.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'informant_questionnaires_completed',
    label: 'Informant Questionnaires Completed',
    description:
      'Sent to the support team when an informant finishes all of their assigned questionnaires.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'comprehensive_questionnaires_completed',
    label: 'All Questionnaires Completed (Patient + Informants)',
    description:
      'Sent to the lead clinician (with support CC) when both the patient AND all informants have completed their questionnaires.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'informant_setup_completed',
    label: 'Informant Setup Completed',
    description:
      'Sent to the support team when a patient completes the informant setup process.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'informant_added',
    label: 'Informant Added',
    description:
      'Sent to the support team when a patient adds a new informant to their account.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'informant_reassigned',
    label: 'Informant Reassigned',
    description:
      'Sent to the support team when a patient reassigns questionnaires to a different informant.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'informant_replaced',
    label: 'Informant Replaced',
    description:
      'Sent to the support team when a patient replaces one informant with a new one.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'informant_email_changed',
    label: 'Informant Email Changed',
    description:
      'Sent to the support team when a patient changes their informant\'s email address.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'ssi_completed',
    label: 'Semi-Structured Interview Completed',
    description:
      'Sent to the support team when a clinician completes a semi-structured interview.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'pad_completed',
    label: 'Post-Assessment Discussion Completed',
    description:
      'Sent to the support team when a clinician completes a post-assessment discussion.',
    allowDisable: true,
    hasCcProvider: false,
  },
  // CYP ADHD assessment progress emails. These mirror the adult
  // ssi_completed / pad_completed alerts but have separate config keys so
  // organisations can toggle them independently. They are module-gated
  // to cypAdhd, so adult-only orgs never see them in the settings UI.
  {
    key: 'cyp_ssi_completed',
    label: 'CYP ADHD MDT Information Available',
    description:
      'Sent to the MDT supporting clinician(s) when both the Clinical Interview '
      + 'and Semi-Structured Interview reports are approved for a CYP ADHD patient, '
      + 'indicating that the information is ready for the MDT review.',
    allowDisable: true,
    hasCcProvider: false,
    modules: ['cypAdhd'],
  },
  {
    key: 'cyp_pad_completed',
    label: 'CYP ADHD Assessment Completed',
    description:
      'Sent to the support team when the Post-Assessment Discussion report is '
      + 'approved for a CYP ADHD patient, indicating the assessment is complete.',
    allowDisable: true,
    hasCcProvider: false,
    modules: ['cypAdhd'],
  },
  {
    key: 'patient_name_updated',
    label: 'Patient Name Updated',
    description:
      'Sent to the support team when a patient updates their first or last name via the Your Information form.',
    allowDisable: true,
    hasCcProvider: false,
  },
  {
    key: 'self_harm_concern',
    label: 'Self-Harm Concern',
    description:
      'Sent immediately to the support team when a patient indicates potential self-harm in the PHQ-9 questionnaire. This notification cannot be disabled for safety reasons.',
    allowDisable: false,
    hasCcProvider: false,
  },
];

/**
 * All notification groups in the order they should appear in the
 * Notification Preferences panel.  Each entry has:
 *   - groupKey:  Firestore key in notificationConfig
 *                (patient | informant | cypGuardian | cypSchoolRep | providerAlerts)
 *   - title:     Section heading shown in the UI
 *   - subtitle:  Short description under the heading
 *   - items:     Array of notification config objects
 *   - modules:   Optional list of assessment modules that must be enabled on
 *                the organisation for the group to be shown. Omitted = always
 *                shown. Keeps CYP-only groups out of adult-only orgs' UI
 *                without touching their stored config.
 */
export const NOTIFICATION_GROUPS = [
  {
    groupKey: 'patient',
    title: 'Patient Notifications',
    subtitle: 'Automated reminders sent to patients',
    items: PATIENT_NOTIFICATIONS,
  },
  {
    groupKey: 'informant',
    title: 'Informant Notifications',
    subtitle: 'Automated reminders sent to informants',
    items: INFORMANT_NOTIFICATIONS,
  },
  {
    groupKey: 'cypGuardian',
    title: 'CYP Parent/Carer Notifications',
    subtitle: 'Automated reminders sent to CYP ADHD parents and carers',
    items: CYP_GUARDIAN_NOTIFICATIONS,
    modules: ['cypAdhd'],
  },
  {
    groupKey: 'cypSchoolRep',
    title: 'CYP School Representative Notifications',
    subtitle: 'Automated reminders sent to nominated school representatives',
    items: CYP_SCHOOL_REP_NOTIFICATIONS,
    modules: ['cypAdhd'],
  },
  {
    groupKey: 'providerAlerts',
    title: 'Provider Alerts',
    subtitle: 'Notifications sent to your support team',
    items: PROVIDER_ALERT_NOTIFICATIONS,
  },
];

/**
 * Resolve which notification groups an organisation should see, given the
 * assessment modules enabled for it.
 *
 * @param {string[]} availableModules - e.g. ['adultAdhd', 'cypAdhd']
 * @returns The NOTIFICATION_GROUPS entries visible to this organisation.
 */
export function getNotificationGroupsForModules(availableModules = []) {
  return NOTIFICATION_GROUPS.filter(
    (group) => !group.modules || group.modules.some((m) => availableModules.includes(m)),
  );
}
